import React, { useState, useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Card,
  Container,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Helmet } from "react-helmet";
import BillingTable from "./HypervisedBillingTable";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

const CustomerList = () => {
  const [billinginfo, setBillinginfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBillingData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/hypervisedbillingview/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch billing data");
        }

        const data = await response.json();
        setBillinginfo(data);
      } catch (err) {
        setError(err.message || "An error occurred while loading billing information");
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, []);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    if (!billinginfo?.results || !Array.isArray(billinginfo.results)) {
      return { totalLabs: 0, totalCost: 0 };
    }

    const totalLabs = billinginfo.results.length;
    const totalCost = billinginfo.results.reduce((sum, lab) => {
      const price = parseFloat(lab.Price) || 0;
      return sum + price;
    }, 0);

    return { totalLabs, totalCost };
  }, [billinginfo]);

  const hasData = billinginfo?.results && Array.isArray(billinginfo.results) && billinginfo.results.length > 0;

  return (
    <>
      <Helmet>
        <title>LabAsService - Hypervised Labs Billing</title>
      </Helmet>
      <Container maxWidth={false} sx={{ py: 6, pb: 8, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
        <Card sx={{ mb: 4 }}>
          {/* Hero Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              p: { xs: 2.5, md: 3 },
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <ReceiptIcon color="primary" sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="h4" component="h1">
                  Hypervised Labs Billing
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View detailed billing information for hypervised lab instances
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Summary Metrics */}
          {!loading && !error && (
            <>
              <Box sx={{ px: { xs: 2.5, md: 3 }, py: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Card variant="outlined" sx={{ flex: 1 }}>
                    <Box sx={{ p: 1.5 }}>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <AccountBalanceIcon color="primary" sx={{ fontSize: 28 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          Total Labs
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.2 }}>
                          {summaryMetrics.totalLabs}
                        </Typography>
                      </Stack>
                    </Box>
                  </Card>
                  <Card variant="outlined" sx={{ flex: 1 }}>
                    <Box sx={{ p: 1.5 }}>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <ReceiptIcon color="info" sx={{ fontSize: 28 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          Total Cost
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.2 }}>
                          ${summaryMetrics.totalCost.toFixed(2)}
                        </Typography>
                      </Stack>
                    </Box>
                  </Card>
                </Stack>
              </Box>
              <Divider />
            </>
          )}

          {/* Content Section */}
          <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
            {/* Error State */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Loading State */}
            {loading && (
              <Box>
                <Stack spacing={2}>
                  <Skeleton variant="rectangular" height={60} />
                  <Skeleton variant="rectangular" height={400} />
                </Stack>
              </Box>
            )}

            {/* Empty State */}
            {!loading && !error && !hasData && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 300,
                  color: 'text.secondary',
                }}
              >
                <ReceiptIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  No Billing Data Available
                </Typography>
                <Typography variant="body2" align="center" sx={{ maxWidth: 400, mt: 1 }}>
                  No hypervised lab billing information found. Billing data will appear here once hypervised labs are created and running.
                </Typography>
              </Box>
            )}

            {/* Billing Table */}
            {!loading && !error && hasData && (
              <BillingTable results={billinginfo.results} />
            )}
          </Box>
        </Card>
      </Container>
    </>
  );
};

export default CustomerList;